import {
  Component,
  inject,
  ViewEncapsulation,
  ChangeDetectorRef,
  OnInit,
  computed,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
  Validators,
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { CommonModule, NgClass } from '@angular/common';
import { TitlePagesComponent } from '../../../../shared/components/title-pages-component/title-pages-component';
import { MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { ActivatedRoute, Router } from '@angular/router';
import { SnackbarService } from '@/app/shared/services/snackbar.service';
import { noSoloEspacios } from '../../../../features/clientes/pages/clientes-form-component/validations';
import {
  CreateUserWithPasswordRequest,
  passwordChangeRequest,
  User,
  UserRequest,
} from '../../services/user.interface';
import { UsersService } from '../../services/users.service';
import { ClientsService } from '../../../../features/clientes/services/clients.service';
import { AppConstants } from '@/app/constants/AppConstants';
import { ThemeService } from '@/app/core/services/theme/theme.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-usuarios-form-component',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgClass,
    TitlePagesComponent,
    MatFormFieldModule,
    MatInputModule,
    MatDialogModule,
    MatButtonModule,
    MatSelectModule,
    MatCheckboxModule,
    MatSlideToggleModule,
    MatIconModule,
  ],
  templateUrl: './usuario-form-component.html',
  styleUrl: './usuario-form-component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class UsuarioFormComponent implements OnInit {
  private formBuilder = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private usersService = inject(UsersService);
  private snackbarService = inject(SnackbarService);
  private clientsService = inject(ClientsService); // Inject ClientsService
  private cdr = inject(ChangeDetectorRef); // Inject ChangeDetectorRef
  private themeService = inject(ThemeService);

  private userData!: UserRequest;
  private user!: User;
  protected id: string | null = '';
  protected submitted = false;
  protected pageTitle = '';
  protected clients: { id: string; name: string }[] = []; // Store clients
  protected isLoadingClients = false; // Add loading state

  protected isChangingPassword = false;
  protected newPassword = null;
  protected confirmPassword = null;
  private passwordChangeRequest: passwordChangeRequest | null = null;
  
  protected showPassword = false;
  protected showConfirmPassword = false;

  protected isAdminUser = false;
  protected hasError = false;

  protected themeContent =  this.themeService.themeContent;
  protected userContent =  computed(() => this.themeContent()?.usersFormComponent)



  formUser = this.formBuilder.group({
    firstName: new FormControl('', [Validators.required, noSoloEspacios()]),
    lastName: new FormControl('', [Validators.required, noSoloEspacios()]),
    username: new FormControl('', [Validators.required, noSoloEspacios()]),
    email: new FormControl('', [Validators.required, Validators.email]),
    //cliente: new FormControl<{ id: string; name: string } | null>(null),
    cliente: new FormControl<{ id: string; name: string } | null>(null),
    password: new FormControl('',  [Validators.required, noSoloEspacios()]),
    confirmPassword: new FormControl('',  [Validators.required, noSoloEspacios()]),
  });

  protected pageData = {
    title: AppConstants.USUARIOS_CREATE_TITLE,
    subTitle: AppConstants.USUARIOS_CREATE_SUBTITLE,
  };

  ngOnInit(): void {
    this.pageTitle = this.route.snapshot.data['title'];
    this.id = this.route.snapshot.paramMap.get('id');

    // Load clients first, then load user
    this.loadClients().then(() => {
      this.loadUser(this.id);
      if (this.id) {
        this.pageData.title = AppConstants.USUARIOS_EDIT_TITLE;
        this.pageData.subTitle = AppConstants.USUARIOS_EDIT_SUBTITLE;
      }
    });
  }

  private loadClients(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.isLoadingClients = true;
      this.clientsService.getAllClients().subscribe({
        next: (data) => {
          this.clients = data.map((client: any) => ({
            id: client.publicId || client.id,
            name: client.name || client.nombre,
          }));
          this.isLoadingClients = false;
          this.cdr.detectChanges();
          resolve();
        },
        error: (err) => {
          console.error('Error fetching clients:', err);
          this.isLoadingClients = false;
          this.snackbarService.showError('Error al cargar los clientes');
          reject(err);
        },
      });
    });
  }


 
  onAdminUserChange(event: any) {
    this.isAdminUser = event.checked;
    this.updateClienteValidation();
  }
  onSubmit() {
    this.submitted = true;
    if (this.formUser.invalid) {
      return;
    }

    // Parse the client data from the form only if not admin
    let clientData: any = {};
    if (!this.isAdminUser && this.formUser.value.cliente) {
      clientData = this.formUser.value.cliente;
    }

    this.userData = {
      id: this.id ?? '',
      username: this.formUser.value.username?.trim() ?? '',
      firstName: this.formUser.value.firstName?.trim() ?? '',
      lastName: this.formUser.value.lastName?.trim() ?? '',
      email: this.formUser.value.email?.trim() ?? '',
      enabled: true,
      groups: this.isAdminUser ? ['Admin-Group'] : ['Client-Group'],
      attributes: this.isAdminUser
        ? null
        : {
            clientName: clientData.name ?? '',
            publicId: clientData.id ?? '',
          },
    };

    const password = this.formUser.value.password;
    const confirmPassword = this.formUser.value.confirmPassword;

    if (this.isChangingPassword && password && confirmPassword) {
      this.passwordChangeRequest = {
        password,
        confirmPassword,
      };
    }

    if (this.id && (this.id !== '' || this.id !== null)) {
      this.usersService.updateUser(this.id, this.userData!).subscribe({
        next: () => {
          this.snackbarService.showSuccess('Usuario editado con éxito');
          if (this.passwordChangeRequest) {
            this.usersService
              .changePassword(this.id!, this.passwordChangeRequest)
              .subscribe({
                error: (err) => {
                  this.snackbarService.showError(
                    'Error al cambiar la contraseña: ' + err.message
                  );
                },
              });
          }
          this.formUser.reset();
          this.usersService.refreshUsers(); // Refresh users list after edit
          this.router.navigate(['/usuarios']);
        },
        error: (err) => {
          this.snackbarService.showError(
            'Error al editar el usuario ' + err.message
          );
        },
      });
    } else {
      // Crear nuevo usuario

      const createRequest: CreateUserWithPasswordRequest = {
        userRequest: this.userData!,
        passwordRequest: {
          password: this.formUser.value.password?.trim() ?? '',
          confirmPassword: this.formUser.value.confirmPassword?.trim() ?? ''
        }
      };

      this.usersService.createUser(createRequest).subscribe({
        next: () => {
          this.snackbarService.showSuccess('Usuario creado con éxito ');
          this.formUser.reset();
          this.usersService.refreshUsers(); 
          this.router.navigate(['/usuarios']);
        },
        error: (err) => {
          this.snackbarService.showError(
            'Error: ' + err.message
          );
        },
      });
    }
  }

  get firstName() {
    return this.formUser.get('firstName');
  }

  get lastName() {
    return this.formUser.get('lastName');
  }

  get username() {
    return this.formUser.get('username');
  }
  get email() {
    return this.formUser.get('email');
  }
  get cliente() {
    return this.formUser.get('cliente');
  }

  // Prevenir espacios en el username durante la escritura
  onKeyPress(event: KeyboardEvent): void {
    // Bloquear espacios (código 32)
    if (event.key === ' ') {
      event.preventDefault();
    }
  }

  onPasswordKeyPress(event: KeyboardEvent): void {
    if (event.key === ' ') {
      event.preventDefault();
    }
    this.isChangingPassword = true;
    this.updatePasswordValidation();

    // Re-validate confirm password when password changes
    setTimeout(() => {
      const confirmPasswordControl = this.formUser.get('confirmPassword');
      confirmPasswordControl?.updateValueAndValidity();
    }, 0);
  }

  onPasswordInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const passwordValue = target.value;
    const confirmPasswordControl = this.formUser.get('confirmPassword');
    const confirmPasswordValue = confirmPasswordControl?.value;

    // If both fields are empty, clear validations
    if (!passwordValue && !confirmPasswordValue) {
      this.isChangingPassword = false;
      this.clearPasswordValidations();
    } else if (passwordValue) {
      this.isChangingPassword = true;
      this.updatePasswordValidation();
    }

    // Re-validate confirm password
    setTimeout(() => {
      confirmPasswordControl?.updateValueAndValidity();
    }, 0);
  }

  onConfirmPasswordInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const confirmPasswordValue = target.value;
    const passwordControl = this.formUser.get('password');
    const passwordValue = passwordControl?.value;

    // If both fields are empty, clear validations
    if (!passwordValue && !confirmPasswordValue) {
      this.isChangingPassword = false;
      this.clearPasswordValidations();
    } else if (confirmPasswordValue || passwordValue) {
      this.isChangingPassword = true;
      this.updatePasswordValidation();
    }
  }

  onPaste(event: ClipboardEvent): void {
  event.preventDefault();
  const clipboardData = event.clipboardData?.getData('text') || '';
  const textWithoutSpaces = clipboardData.replace(/\s/g, '');
  
  // Obtener el input que disparó el evento
  const input = event.target as HTMLInputElement;
  const inputId = input.id;
  
  // Determinar qué control actualizar basado en el ID del input
  let control: AbstractControl | null = null;
  
  switch(inputId) {
    case 'username':
      control = this.formUser.get('username');
      break;
    case 'password':
      control = this.formUser.get('password');
      break;
    default:
      // Para otros inputs, podrías tener lógica adicional
      return;
  }
  
  if (control) {
    const currentValue = control.value || '';
    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    
    const newValue =
      currentValue.slice(0, start) +
      textWithoutSpaces +
      currentValue.slice(end);
    
    control.setValue(newValue);
    
    // Si es el campo password, también actualizamos el estado
    if (inputId === 'password') {
      this.isChangingPassword = true;
      this.updatePasswordValidation();
      
      // Actualizar validación del confirm password
      setTimeout(() => {
        this.formUser.get('confirmPassword')?.updateValueAndValidity();
      }, 0);
    }
  }
}

  private loadUser(id: string | null): void {
    if (id !== null) {
      this.usersService.getUser(id).subscribe({
        next: (usuario) => {
          this.user = usuario;

          this.isAdminUser = usuario.group?.includes('Admin-Group') || false;

          this.updateClienteValidation();

          // Find the matching client to set the select value
          const attributes = usuario.attributes as any;
          const clientName =
            attributes?.['clientName'] || attributes?.['Cliente'] || '';
          const publicId = attributes?.['publicId'] || '';

          // Find the client that matches either by name or publicId
          const matchingClient = this.clients.find(
            (client) => client.name === clientName || client.id === publicId
          );

          this.formUser.patchValue({
            firstName: usuario.firstName,
            lastName: usuario.lastName,
            username: usuario.username,
            email: usuario.email,
            cliente: matchingClient || null,
          });
        },
        error: (err) => {
          console.error('Error al obtener usuario desde el servicio:', err);
        },
      });
    } else {
      // For new users, default to client user (not admin)
      this.isAdminUser = false;
      this.updateClienteValidation();
    }
  }

  // Add method to toggle user type (for new users)
  toggleUserType(): void {
    this.isAdminUser = !this.isAdminUser;
    this.updateClienteValidation();
    this.onAdminUserChange({ checked: this.isAdminUser });
  }

  private updateClienteValidation(): void {
    const clienteControl = this.formUser.get('cliente');
    const passwordControl = this.formUser.get('password');
    const confirmPasswordControl = this.formUser.get('confirmPassword');

    if (this.isAdminUser) {
      // Remove validation for admin users
      clienteControl?.clearValidators();
      clienteControl?.setValue(null);
    } else {
      // Add validation for client users
      clienteControl?.setValidators([Validators.required]);
    }

    // Clear password validations for existing users (only required for new users)
    if (this.id) {
      passwordControl?.clearValidators();
      confirmPasswordControl?.clearValidators();
    }

    clienteControl?.updateValueAndValidity();
    passwordControl?.updateValueAndValidity();
    confirmPasswordControl?.updateValueAndValidity();
  }

  private updatePasswordValidation(): void {
    const passwordControl = this.formUser.get('password');
    const confirmPasswordControl = this.formUser.get('confirmPassword');
    if (this.isChangingPassword) {
      passwordControl?.setValidators([
        Validators.required,
        Validators.minLength(6),
      ]);
      confirmPasswordControl?.setValidators([
        Validators.required,
        this.passwordMatchValidator(),
      ]);
    }

    passwordControl?.updateValueAndValidity();
    confirmPasswordControl?.updateValueAndValidity();
  }

  private clearPasswordValidations(): void {
    const passwordControl = this.formUser.get('password');
    const confirmPasswordControl = this.formUser.get('confirmPassword');

    passwordControl?.clearValidators();
    confirmPasswordControl?.clearValidators();

    passwordControl?.updateValueAndValidity();
    confirmPasswordControl?.updateValueAndValidity();
  }

  // Custom validator to check if passwords match
  private passwordMatchValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const password = this.formUser?.get('password')?.value;
      const confirmPassword = control.value;

      if (password && confirmPassword && password !== confirmPassword) {
        return { passwordMismatch: true };
      }

      return null;
    };
  }

  compareClients(client1: { id: string; name: string } | null, client2: { id: string; name: string } | null): boolean {
    return client1 && client2 ? client1.id === client2.id : client1 === client2;
  }

  onCancel(): void {
    this.router.navigate(['/usuarios']);
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPasswordVisibility() {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

}
